import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import moment from 'moment';
import {
  type ColumnFiltersState,
  type SortingState,
} from '@tanstack/react-table';
import { Link } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type {
  InitialTableState,
  TableState,
} from 'components/data-table/types';
import {
  createDiscountRateScenarioSearchAtom,
  type DiscountRateScenarioParams,
} from './atoms';
import type { DiscountRateScenarioResult } from './types';
import { DiscountRateScenariosFilters } from './discount-rate-scenarios-filters';
import {
  DiscountRateSummaryCards,
  type SummaryCardType,
} from './summary-cards';
import { DEFAULT_PARAMS, SORT_FIELD_MAP, columnFiltersToParams } from './utils';
import styles from './discount-rate-scenarios-content.module.scss';

type DrsRow = DiscountRateScenarioResult & Record<string, unknown>;

const columns: DataTableColumnDef<DrsRow>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 280,
    cell: ({ row }) => <Link to={row.original.href}>{row.original.name}</Link>,
  },
  {
    accessorKey: 'rateEntryCount',
    header: '# Rate Entries',
    size: 120,
    meta: { type: 'integer' },
    enableSorting: false,
  },
  {
    accessorKey: 'areaOverrideCount',
    header: '# Area Overrides',
    size: 120,
    meta: { type: 'integer' },
    enableSorting: false,
  },
  {
    accessorKey: 'effectiveDate',
    header: 'Effective Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'createdBy',
    header: 'Created By',
    size: 160,
    meta: { type: 'enum' },
    enableSorting: false,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 180,
    meta: { type: 'date' },
  },
  {
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 180,
    meta: { type: 'date' },
  },
];

const initialTableState: InitialTableState = {
  columnPinning: { left: ['name'] },
  sortColumn: 'createdAt',
  sortDirection: 'desc',
};

/**
 * Main content for the DRS list page — summary cards + DataTable with filters.
 * @return {JSX.Element} The list page content
 */
const DiscountRateScenariosContent: FC = () => {
  const { searchAtom } = useMemo(
    () => createDiscountRateScenarioSearchAtom(),
    []
  );
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const [params, setParams] =
    useState<DiscountRateScenarioParams>(DEFAULT_PARAMS);

  useEffect(() => {
    setSearchPayload(() => params);
  }, [params, setSearchPayload]);

  const updateParams = useCallback(
    (updates: Partial<DiscountRateScenarioParams>) => {
      setParams((current) => ({ ...current, page: 1, ...updates }));
    },
    []
  );

  const handleColumnFilterChange = useCallback(
    (filters: ColumnFiltersState) => {
      updateParams(columnFiltersToParams(filters));
    },
    [updateParams]
  );

  const handleSortChange = useCallback(
    (sorting: SortingState) => {
      if (sorting.length === 0) {
        updateParams({ sortAttribute: 'created_at', sortDirection: 'DESC' });
      } else {
        const sort = sorting[0];
        updateParams({
          sortAttribute: SORT_FIELD_MAP[sort.id] ?? sort.id,
          sortDirection: sort.desc ? 'DESC' : 'ASC',
        });
      }
    },
    [updateParams]
  );

  const handleGlobalSearchChange = useCallback(
    (search: string) => {
      const newText = search || undefined;
      if (params.text === newText) return;
      updateParams({ text: newText });
    },
    [params.text, updateParams]
  );

  const handleSummaryCardClick = useCallback(
    (card: SummaryCardType) => {
      const now = moment();
      const daysBack = { last24h: 1, last7d: 7, lastMonth: 30 };
      const start = now.clone().subtract(daysBack[card], 'days');
      updateParams({
        createdAt: {
          start: start.format('YYYY-MM-DD'),
          end: now.format('YYYY-MM-DD'),
        },
      });
    },
    [updateParams]
  );

  const results = searchResult.data?.results ?? [];
  const totalCount = searchResult.data?.totalCount ?? 0;

  const handleLoadData = useCallback(({ page, pageSize }: TableState) => {
    setParams((current) => ({ ...current, page, pageSize }));
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <DiscountRateSummaryCards
        aggregations={searchResult.data?.aggregations}
        onCardClick={handleSummaryCardClick}
      />
      <div className={styles.tableWrapper}>
        <DataTable<DrsRow>
          data={results as DrsRow[]}
          columns={columns as DataTableColumnDef<DrsRow>[]}
          getRowId={(row) => String(row.id)}
          initialTableState={initialTableState}
          persistenceKey="discount-rate-scenarios-list-table"
          config={{ columnFilters: false, columnReordering: false }}
          isLoading={searchResult.loading}
          error={searchResult.error ?? null}
          scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
          onPaginationChange={handleLoadData}
          onSortChange={(s) => handleSortChange(s.sorting)}
          onGlobalSearchChange={(s) => handleGlobalSearchChange(s.globalFilter)}
          onFilterChange={(s) => handleColumnFilterChange(s.columnFilters)}
          searchPlaceholder="Search by name..."
          renderFilterPanel={() => (
            <DiscountRateScenariosFilters facets={searchResult.data?.facets} />
          )}
        />
      </div>
    </div>
  );
};

export default DiscountRateScenariosContent;
